import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Save, Loader2, CheckCircle, Globe, Eye, EyeOff, History, Languages, AlertTriangle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminDb } from '../lib/adminDb';

interface LegalEntry {
  id: string;
  type: string;
  language: string;
  content_html: string;
  version: number;
  translation_status: string; // 'master' | 'translated' | 'outdated'
  updated_at: string;
}

interface HistoryEntry {
  id: string;
  type: string;
  language: string;
  version: number;
  content_html: string;
  changed_by: string;
  created_at: string;
}

const LEGAL_TYPES = [
  { key: 'impressum', label: 'Impressum', icon: '📋' },
  { key: 'datenschutz', label: 'Datenschutzbestimmungen', icon: '🔒' },
  { key: 'agb', label: 'AGB', icon: '📄' },
  { key: 'cookies', label: 'Cookie-Richtlinien', icon: '🍪' },
] as const;

const LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', master: true },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'prs', label: 'دری', flag: '🇦🇫' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷' },
];

const LANG_NAMES: Record<string, string> = {
  de: 'German', en: 'English', tr: 'Turkish', ar: 'Arabic',
  prs: 'Dari (Afghan Persian)', ur: 'Urdu', uk: 'Ukrainian', hi: 'Hindi', fa: 'Farsi (Persian)',
};

export const LegalContentPage: React.FC = () => {
  const [entries, setEntries] = useState<LegalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('impressum');
  const [selectedLang, setSelectedLang] = useState<string>('de');
  const [content, setContent] = useState(''); // Only used for HTML code textarea mode
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(true);
  const [dirty, setDirty] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null); // Ref-based editor — no React re-renders on input
  const [translating, setTranslating] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [translationProgress, setTranslationProgress] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load all entries
  const loadEntries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('legal_content')
      .select('*')
      .order('type')
      .order('language');
    if (data) setEntries(data as LegalEntry[]);
    setLoading(false);
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Load content when selection changes
  useEffect(() => {
    const entry = entries.find(e => e.type === selectedType && e.language === selectedLang);
    const html = entry?.content_html || '';
    setContent(html);
    setDirty(false);
    setShowHistory(false);
    // Set editor content directly via DOM ref (avoids React re-render issues)
    if (editorRef.current) {
      editorRef.current.innerHTML = html || '<p style="color: #999; font-style: italic;">Klicken um zu bearbeiten. Text mit Formatierung einfügen (Copy & Paste).</p>';
    }
  }, [selectedType, selectedLang, entries]);

  // Get current entry info
  const currentEntry = entries.find(e => e.type === selectedType && e.language === selectedLang);
  const masterEntry = entries.find(e => e.type === selectedType && e.language === 'de');
  const currentVersion = currentEntry?.version || 0;
  const masterVersion = masterEntry?.version || 0;
  const isOutdated = selectedLang !== 'de' && currentEntry && masterEntry && currentEntry.version < masterEntry.version;

  // Read current content from the editor (ref-based for WYSIWYG, state for HTML code)
  const getCurrentContent = (): string => {
    if (preview && editorRef.current) {
      return editorRef.current.innerHTML;
    }
    return content;
  };

  const handleSave = async (newVersion = false) => {
    setSaving(true);
    const currentContent = getCurrentContent();
    try {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user?.id;
      const userEmail = session.data.session?.user?.email || 'unknown';

      const version = newVersion ? (currentEntry?.version || 0) + 1 : (currentEntry?.version || 1);
      const isMaster = selectedLang === 'de';

      // Save history before update (if entry exists)
      if (currentEntry?.content_html) {
        const { error: histErr } = await adminDb.from('legal_content_history').insert({
          legal_content_id: currentEntry.id,
          type: selectedType,
          language: selectedLang,
          version: currentEntry.version || 1,
          content_html: currentEntry.content_html,
          changed_by: userEmail,
        });
        if (histErr) {
          console.error('History insert failed:', histErr);
          // Non-blocking — continue saving even if history fails
        }
      }

      if (currentEntry) {
        const { error: updateErr } = await adminDb.from('legal_content').update({
          content_html: currentContent,
          version,
          translation_status: isMaster ? 'master' : 'translated',
          updated_at: new Date().toISOString(),
          updated_by: userId || null,
        }).eq('id', currentEntry.id);
        if (updateErr) throw new Error(`Update fehlgeschlagen: ${updateErr.message}`);
      } else {
        const { error: insertErr } = await adminDb.from('legal_content').insert({
          type: selectedType,
          language: selectedLang,
          content_html: currentContent,
          version: isMaster ? version : masterVersion,
          translation_status: isMaster ? 'master' : 'translated',
          updated_by: userId || null,
        });
        if (insertErr) throw new Error(`Insert fehlgeschlagen: ${insertErr.message}`);
      }

      // If German master version was incremented, mark all other languages as outdated
      if (isMaster && newVersion) {
        const otherEntries = entries.filter(e => e.type === selectedType && e.language !== 'de');
        for (const oe of otherEntries) {
          await adminDb.from('legal_content').update({
            translation_status: 'outdated',
          }).eq('id', oe.id);
        }
      }

      // Log audit (non-blocking)
      adminDb.from('audit_logs').insert({
        admin_email: userEmail,
        action: newVersion ? 'legal_content.new_version' : 'legal_content.updated',
        resource_type: 'legal_content',
        resource_id: `${selectedType}/${selectedLang}`,
        old_value: { version: currentEntry?.version || 0 },
        new_value: { version, content_length: currentContent.length },
      }).then(({ error }: any) => {
        if (error) console.error('Audit log failed:', error);
      });

      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 2000);
      await loadEntries();
    } catch (err: any) {
      console.error('Save failed:', err);
      alert(`Fehler beim Speichern: ${err?.message || 'Unbekannter Fehler'}`);
    } finally {
      setSaving(false);
    }
  };

  // Translate from German master to target language via AI
  const translateToLanguage = async (targetLang: string, silent = false) => {
    if (!masterEntry?.content_html) {
      if (!silent) alert('Kein deutscher Mastertext vorhanden.');
      return;
    }

    const langName = LANG_NAMES[targetLang] || targetLang;
    setTranslating(true);
    setTranslationProgress(`Übersetze nach ${LANGUAGES.find(l => l.code === targetLang)?.label}...`);

    const MAX_RETRIES = 2;
    let lastError = '';

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        
        // AbortController for timeout (120s — large legal texts need time)
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 120_000);

        const response = await fetch('/api/ai/translate-legal', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            htmlContent: masterEntry.content_html,
            targetLanguage: langName,
            targetLangCode: targetLang,
            type: selectedType,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Translation failed' }));
          throw new Error(err.error || `HTTP ${response.status}`);
        }

        const { translatedHtml } = await response.json();

        // Save translated content
        const userId = session.data.session?.user?.id;
        const existingTarget = entries.find(e => e.type === selectedType && e.language === targetLang);

        if (existingTarget) {
          if (existingTarget.content_html) {
            await adminDb.from('legal_content_history').insert({
              legal_content_id: existingTarget.id,
              type: selectedType,
              language: targetLang,
              version: existingTarget.version || 1,
              content_html: existingTarget.content_html,
              changed_by: 'AI Translation',
            });
          }
          await adminDb.from('legal_content').update({
            content_html: translatedHtml,
            version: masterEntry.version,
            translation_status: 'translated',
            updated_at: new Date().toISOString(),
            updated_by: userId || null,
          }).eq('id', existingTarget.id);
        } else {
          await adminDb.from('legal_content').insert({
            type: selectedType,
            language: targetLang,
            content_html: translatedHtml,
            version: masterEntry.version,
            translation_status: 'translated',
            updated_by: userId || null,
          });
        }

        await loadEntries();
        
        if (selectedLang === targetLang || !translatingAll) {
          setSelectedLang(targetLang);
        }
        setTranslating(false);
        setTranslationProgress('');
        return; // Success — exit retry loop
      } catch (err: any) {
        lastError = err.name === 'AbortError' ? 'Timeout (>120s)' : err.message;
        console.error(`Translation to ${targetLang} attempt ${attempt}/${MAX_RETRIES}:`, lastError);
        if (attempt < MAX_RETRIES) {
          // Exponential backoff: 5s, then 10s
          const delay = attempt * 5000;
          setTranslationProgress(`⚠️ Retry ${LANGUAGES.find(l => l.code === targetLang)?.label} in ${delay/1000}s...`);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }

    // All retries failed
    if (!silent) {
      alert(`Übersetzungsfehler (${LANG_NAMES[targetLang]}): ${lastError}`);
    }
    setTranslating(false);
    setTranslationProgress('');
    throw new Error(lastError); // Re-throw so translateAll can track failures
  };

  // Translate to ALL languages with retry and rate-limit spacing
  const translateAllLanguages = async () => {
    if (!masterEntry?.content_html) {
      alert('Kein deutscher Mastertext vorhanden.');
      return;
    }

    if (!confirm(`Mastertext (v${masterEntry.version}) in alle ${LANGUAGES.length - 1} Sprachen übersetzen?\n\nDies kann 3-5 Minuten dauern bei längeren Texten.`)) return;

    setTranslatingAll(true);
    const otherLangs = LANGUAGES.filter(l => l.code !== 'de');
    const failed: string[] = [];
    const succeeded: string[] = [];
    
    for (let i = 0; i < otherLangs.length; i++) {
      const lang = otherLangs[i];
      setTranslationProgress(`${lang.flag} ${lang.label}... (${i + 1}/${otherLangs.length})`);
      try {
        await translateToLanguage(lang.code, true); // silent=true, no alerts
        succeeded.push(`${lang.flag} ${lang.label}`);
      } catch (err) {
        console.error(`Translation to ${lang.code} failed:`, err);
        failed.push(`${lang.flag} ${lang.label}`);
      }
      // Rate-limit spacing: wait 3s between translations to avoid Gemini API limits
      if (i < otherLangs.length - 1) {
        setTranslationProgress(`Warte...`);
        await new Promise(r => setTimeout(r, 3000));
      }
    }

    setTranslatingAll(false);
    setTranslationProgress('');
    setSelectedLang('de');
    
    if (failed.length === 0) {
      alert(`✅ Alle ${succeeded.length} Übersetzungen erfolgreich!`);
    } else {
      alert(`⚠️ ${succeeded.length} erfolgreich, ${failed.length} fehlgeschlagen:\n${failed.join('\n')}\n\nBitte die fehlgeschlagenen Sprachen einzeln übersetzen.`);
    }
  };

  // Load version history
  const loadHistory = async () => {
    setHistoryLoading(true);
    const { data } = await supabase
      .from('legal_content_history')
      .select('*')
      .eq('type', selectedType)
      .eq('language', selectedLang)
      .order('created_at', { ascending: false })
      .limit(10);
    if (data) setHistory(data as HistoryEntry[]);
    setHistoryLoading(false);
    setShowHistory(true);
  };

  const restoreVersion = async (histEntry: HistoryEntry) => {
    if (!confirm(`Version ${histEntry.version} wiederherstellen?`)) return;
    setContent(histEntry.content_html);
    if (editorRef.current) editorRef.current.innerHTML = histEntry.content_html;
    setDirty(true);
    setShowHistory(false);
  };

  const currentType = LEGAL_TYPES.find(t => t.key === selectedType)!;
  const currentLang = LANGUAGES.find(l => l.code === selectedLang)!;

  // Count how many languages have content for each type
  const getContentStatus = (type: string) => {
    const typeEntries = entries.filter(e => e.type === type && e.content_html?.trim().length > 0);
    return typeEntries.length;
  };

  const getOutdatedCount = (type: string) => {
    return entries.filter(e => e.type === type && e.translation_status === 'outdated').length;
  };

  return (
    <div>
      <div className="page-header">
        <h1><FileText size={24} /> Rechtstexte</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Auto-translate all languages button (only for German master) */}
          {selectedLang === 'de' && masterEntry?.content_html && (
            <button
              className="btn btn-secondary"
              onClick={translateAllLanguages}
              disabled={translating || translatingAll || saving}
              title="Mastertext in alle Sprachen übersetzen"
            >
              <Languages size={16} />
              {translatingAll ? translationProgress : 'Alle Sprachen übersetzen'}
            </button>
          )}
          <button
            className={`btn ${preview ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              if (preview) {
                // Switching from WYSIWYG → HTML code: read editor content into state
                if (editorRef.current) setContent(editorRef.current.innerHTML);
              } else {
                // Switching from HTML code → WYSIWYG: write state into editor ref
                // (editor will be mounted by React, useEffect handles initial set)
                setTimeout(() => {
                  if (editorRef.current) editorRef.current.innerHTML = content;
                }, 0);
              }
              setPreview(!preview);
            }}
          >
            {preview ? <EyeOff size={16} /> : <Eye size={16} />}
            {preview ? 'HTML-Code' : 'Vorschau & Editor'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={loadHistory}
            disabled={historyLoading}
          >
            <History size={16} />
            Versionen
          </button>
          {selectedLang === 'de' ? (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => handleSave(false)}
                disabled={saving || !dirty}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                Speichern
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(true)}
                disabled={saving || !dirty}
                title="Neue Version erstellen und bestehende Übersetzungen als veraltet markieren"
              >
                {saved ? <CheckCircle size={16} /> : '🚀'}
                Neue Version v{(masterVersion || 0) + 1}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => translateToLanguage(selectedLang)}
                disabled={translating || !masterEntry?.content_html}
                title="Vom deutschen Mastertext übersetzen"
              >
                {translating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Languages size={16} />}
                {translating ? translationProgress : 'Aus DE übersetzen'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(false)}
                disabled={saving || !dirty}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
                  saved ? <CheckCircle size={16} /> : <Save size={16} />}
                {saved ? 'Gespeichert!' : 'Speichern'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Outdated warning */}
      {isOutdated && (
        <div className="card" style={{
          marginBottom: 16, padding: '12px 16px',
          borderLeft: '4px solid #f59e0b',
          background: '#fffbeb',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
          <div>
            <strong>Übersetzung veraltet!</strong> Diese Übersetzung ist auf Version {currentVersion}, der deutsche Master ist auf Version {masterVersion}.
            <button
              className="btn btn-sm btn-secondary"
              style={{ marginLeft: 12 }}
              onClick={() => translateToLanguage(selectedLang)}
              disabled={translating}
            >
              {translating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Languages size={14} />}
              Jetzt aktualisieren
            </button>
          </div>
        </div>
      )}

      {/* Type + Language Selector */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* Type tabs */}
        <div style={{ display: 'flex', gap: 8, flex: 1 }}>
          {LEGAL_TYPES.map(type => (
            <button
              key={type.key}
              onClick={() => setSelectedType(type.key)}
              className={`btn ${selectedType === type.key ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 4, padding: '12px 16px', position: 'relative',
              }}
            >
              <span style={{ fontSize: 20 }}>{type.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{type.label}</span>
              {/* Content count badge */}
              {getContentStatus(type.key) > 0 && (
                <span style={{
                  position: 'absolute', top: 4, right: 4,
                  background: getOutdatedCount(type.key) > 0 ? '#f59e0b' : '#22c55e',
                  color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                }}>
                  {getContentStatus(type.key)}/{LANGUAGES.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Language selector */}
        <div style={{ position: 'relative' }}>
          <select
            className="input"
            value={selectedLang}
            onChange={e => setSelectedLang(e.target.value)}
            style={{ paddingRight: 40, minWidth: 200, appearance: 'none' }}
          >
            {LANGUAGES.map(l => {
              const entry = entries.find(e => e.type === selectedType && e.language === l.code);
              const status = entry?.translation_status === 'outdated' ? ' ⚠️' : entry?.content_html ? ' ✅' : '';
              return (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.label}{l.master ? ' (Master)' : ''}{status}
                </option>
              );
            })}
          </select>
          <Globe size={14} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.4 }} />
        </div>
      </div>

      {/* Version info bar */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 16, padding: '8px 16px',
        background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 12, alignItems: 'center',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <History size={14} /> Version: <strong>v{currentVersion || '—'}</strong>
        </span>
        {selectedLang === 'de' ? (
          <span style={{ color: '#16a34a', fontWeight: 600 }}>🏠 Master</span>
        ) : (
          <span style={{
            color: currentEntry?.translation_status === 'outdated' ? '#f59e0b' :
                   currentEntry?.translation_status === 'translated' ? '#16a34a' : '#999',
            fontWeight: 600,
          }}>
            {currentEntry?.translation_status === 'outdated' ? '⚠️ Veraltet' :
             currentEntry?.translation_status === 'translated' ? '✅ Übersetzt' : '—'}
          </span>
        )}
        {masterEntry && selectedLang !== 'de' && (
          <span className="text-muted">Master: v{masterVersion}</span>
        )}
        {currentEntry?.updated_at && (
          <span className="text-muted" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} /> {new Date(currentEntry.updated_at).toLocaleString('de-DE')}
          </span>
        )}
      </div>

      {/* Version History Panel */}
      {showHistory && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}><History size={18} /> Versionshistorie — {currentType.label} ({currentLang.label})</h3>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowHistory(false)}>Schließen</button>
          </div>
          {historyLoading ? (
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', opacity: 0.3 }} />
          ) : history.length === 0 ? (
            <p className="text-muted text-sm">Keine Versionshistorie vorhanden.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map(h => (
                <div key={h.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 16px', background: 'var(--bg-secondary)', borderRadius: 8,
                  border: '1px solid var(--border-light)',
                }}>
                  <div>
                    <strong>Version {h.version}</strong>
                    <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                      {new Date(h.created_at).toLocaleString('de-DE')} — {h.changed_by}
                    </span>
                    <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                      ({h.content_html.length} Zeichen)
                    </span>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => restoreVersion(h)}>
                    Wiederherstellen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor / Preview */}
      <div className="card" style={{ minHeight: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{currentType.icon} {currentType.label} — {currentLang.flag} {currentLang.label}</h3>
          {dirty && (
            <span style={{
              background: '#fef3c7', color: '#92400e',
              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            }}>
              Ungespeicherte Änderungen
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', opacity: 0.3 }} />
          </div>
        ) : preview ? (
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => {
              // Only track dirty state — do NOT sync innerHTML to React state
              // This prevents React re-renders from breaking large paste operations
              if (!dirty) setDirty(true);
            }}
            style={{
              border: '1px solid var(--border)', borderRadius: 8, padding: 24,
              minHeight: 400, background: 'var(--bg-secondary)', lineHeight: 1.7, fontSize: 14,
              outline: 'none', cursor: 'text', transition: 'border-color 0.2s',
            }}
            onFocus={e => { (e.target as HTMLDivElement).style.borderColor = '#E03705'; }}
            onBlur={e => {
              (e.target as HTMLDivElement).style.borderColor = 'var(--border)';
              // Sync to state on blur so textarea (HTML code mode) stays in sync
              setContent(e.target.innerHTML);
            }}
          />
        ) : (
          <textarea
            value={content}
            onChange={e => { setContent(e.target.value); setDirty(true); }}
            placeholder={`HTML-Inhalt für ${currentType.label} (${currentLang.label}) eingeben...\n\nBeispiel:\n<p><strong>§ 1 Titel</strong><br/>\nInhalt des Absatzes.</p>`}
            style={{
              width: '100%', minHeight: 400, padding: 16,
              border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 13,
              lineHeight: 1.6, resize: 'vertical', outline: 'none', transition: 'border-color 0.2s',
            }}
            onFocus={e => { e.target.style.borderColor = '#E03705'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
          />
        )}
      </div>

      {/* Translation status overview */}
      <div className="card" style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Globe size={16} /> Übersetzungsstatus — {currentType.label}
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {LANGUAGES.map(lang => {
            const entry = entries.find(e => e.type === selectedType && e.language === lang.code);
            const hasContent = entry?.content_html?.trim();
            const status = entry?.translation_status;
            return (
              <div
                key={lang.code}
                onClick={() => setSelectedLang(lang.code)}
                style={{
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${selectedLang === lang.code ? '#E03705' : 'var(--border-light)'}`,
                  background: !hasContent ? 'var(--bg-secondary)' : status === 'outdated' ? '#fffbeb' : 'var(--bg-secondary)',
                  transition: 'border-color 0.2s',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  {lang.flag} {lang.label}
                </div>
                <div style={{ fontSize: 11 }}>
                  {!hasContent ? (
                    <span style={{ color: '#999' }}>— Kein Inhalt</span>
                  ) : lang.master ? (
                    <span style={{ color: '#16a34a' }}>🏠 Master v{entry?.version || 1}</span>
                  ) : status === 'outdated' ? (
                    <span style={{ color: '#f59e0b' }}>⚠️ v{entry?.version} (veraltet)</span>
                  ) : (
                    <span style={{ color: '#16a34a' }}>✅ v{entry?.version}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hint */}
      <div className="card" style={{ marginTop: 16, padding: '12px 16px' }}>
        <p className="text-xs text-muted" style={{ margin: 0 }}>
          💡 <strong>Workflow:</strong> Bearbeite den deutschen Mastertext → klicke „Neue Version" → klicke „Alle Sprachen übersetzen".
          Die KI übersetzt den HTML-Inhalt unter Beibehaltung der Formatierung. Alle Änderungen werden sofort in der App übernommen.
          Übersetzungen können auch manuell angepasst werden.
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
